import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { timer, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { DetectDeviceService } from 'src/app/services/utils/detect-device.service';
import { BonusListItem, BonusQueueItem, BonusStatus } from 'src/app/services/wallet/wallet.models';
import { BonusQueueService, } from '../../../services/wallet/bonus-queue.service';
import { BasePageComponentWithDialogs } from '../../base-page/base-page.component';
import { Router } from '@angular/router';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ActivePrizesComponent } from '../active-prizes/active-prizes.component';
import { RewardKeys } from '../pending-rewards/pending-rewards.component';
import { PendingReward } from 'src/app/services/prizes/prizes.models';


const cancelConfirmationMsg: string = $localize`:@@cancelBonusConfirmationMsg:Please note that bonus balance will be removed along with all bonus wins. Do you want to proceed?`;
const activateConfirmationMsg: string = $localize`:@@activateBonusConfirmationMsg:By activating this new bonus, your current bonus and any associated winnings will be lost. Are you sure you want to proceed?`;
const errorMessage: string = $localize`:@@bonusErrorMessage-luckera:Please use the current bonus to activate the next one.`;

@Component({
  selector: 'app-bonus-queue',
  templateUrl: './bonus-queue.component.html',
  styleUrls: ['./bonus-queue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BonusQueueComponent extends BasePageComponentWithDialogs {

  constructor(
    private bonusQueueService: BonusQueueService,
    dialog: MatDialog,
    private detectDesktopService: DetectDeviceService,
    protected _ngZone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId,
  ) { super(dialog, detectDesktopService) }

  bonusQueue: BonusQueueItem[];
  bonusList: BonusListItem[];
  activeBonusList: BonusListItem[];
  isDesktop: boolean = this.detectDesktopService.isDesktop();
  isAllowedRender: boolean = false;
  disableOnInteraction: boolean = false;

  @ViewChild("freespinsprizes") freeSpinsActivePrizeCmp: ActivePrizesComponent;

  // Snapshot of the free spin rows before a free spins reward was activated; set only while a scroll is still owed for it.
  private freeSpinKeysBeforeActivation: string[] | null = null;
  private freeSpinScrollAttemptsLeft: number = 0;
  // The success popup blocks page scrolling and restores the old position on close, so no scroll may run while it is open.
  private isSuccessPopupOpen: boolean = false;

  count: number = 0;
  public displayedColumns: string[] = ['date', 'amount', 'status', 'valid'];
  public displayedColumnsBonusList: string[] = ['exp-date', 'amount', 'status', 'turnover'];
  public displayedColumnsBonusActive: string[] = ['exp-date', 'amount', 'status', 'turnover', 'wageringReq'];

  ngOnInit(): void {
    console.log("platform server", isPlatformServer(this.platformId))
    if (!isPlatformServer(this.platformId)) {
      console.log("it works in browser")
      this.isAllowedRender = true;
      this.refreshBonusQueue();
      this.refreshBonusList();
    }
  }

  refreshBonusQueue() {
    this.bonusQueueService.getBonusQueue().pipe(take(1)).subscribe(resp => {
      this.bonusQueue = resp
      this.changeDetectorRef.markForCheck();
    })
  }

  getTournoverPercentage(bonus: BonusListItem) {
    const turnoverSum = Number(bonus.turnover_sum) || 0; // Use the value or default to 0
    const redemptionAmount = Number(bonus.redemption_amount) || 0; // Use the value or default to 0

    // Avoid division by zero
    if (redemptionAmount === 0) {
      return "0.00"; // Return 0% as a string
    }

    let value = turnoverSum / redemptionAmount;
    let finalValue = value * 100.;
    if (finalValue > 99. && finalValue < 100.) finalValue = 99;
    return finalValue.toFixed(2);
  }

  refreshAll(reward?: PendingReward) {
    if (reward?.prizeType?.toLowerCase() === RewardKeys.FREESPINS && this.freeSpinsActivePrizeCmp) {
      this.freeSpinKeysBeforeActivation = this.freeSpinsActivePrizeCmp.getFreeSpinKeys();
      this.freeSpinScrollAttemptsLeft = 3;
    }
    // onAccept fires just before PendingRewardsComponent opens the success popup for every reward.
    if (reward) {
      this.isSuccessPopupOpen = true;
    }
    timer(1000, 2000).pipe(take(3)).subscribe(resp => {
      this.refreshBonusQueue();
      this.refreshBonusList();
      this.freeSpinsActivePrizeCmp.refreshActivePrizes();
    })
  }

  onActivePrizesRendered() {
    if (!this.freeSpinKeysBeforeActivation) return;
    this.freeSpinScrollAttemptsLeft--;
    this.tryFreeSpinScroll();
  }

  onSuccessDialogClosed() {
    this.isSuccessPopupOpen = false;
    this.tryFreeSpinScroll();
  }

  // Runs on every refresh render and on popup close, so whichever of the two happens last performs the scroll.
  private tryFreeSpinScroll() {
    if (!this.freeSpinKeysBeforeActivation || this.isSuccessPopupOpen) return;
    const scrolled = this.freeSpinsActivePrizeCmp.scrollToNewFreeSpin(this.freeSpinKeysBeforeActivation);
    if (!scrolled && this.freeSpinScrollAttemptsLeft <= 0) {
      // The last refresh still showed no new row (e.g. the API was slow to add it), so show the section anyway.
      this.freeSpinsActivePrizeCmp.scrollToFreeSpinsSection();
    }
    // Clearing the snapshot completes the scroll, so later refreshes only re-render.
    if (scrolled || this.freeSpinScrollAttemptsLeft <= 0) {
      this.freeSpinKeysBeforeActivation = null;
    }
  }

  refreshBonusList() {
    this.bonusQueueService.getBonusList(true).pipe(take(1)).subscribe(resp => {
      this.bonusList = resp.slice(0, 10).filter(item => item.raw_status !== BonusStatus.ACTIVE)
      this.activeBonusList = resp.filter(item => item.raw_status === BonusStatus.ACTIVE)
      this.disableOnInteraction = false;
      this.changeDetectorRef.markForCheck();
    })
  }

  bonusTerms(url: string) {
    this.router.navigate([url]);
  }

  confirmCancelation(bonusRef: string) {
    this.bonusQueueService.cancelActiveBonus(bonusRef).pipe(take(1)).subscribe({
      next: (resp) => {
        this._ngZone.runOutsideAngular(() => {
          this.refreshBonusQueue();
          this.refreshBonusList();
        });
      },
      error: (error) => {
        this.openErrorDialog(error.message);
        this.disableOnInteraction = false;
      }
    });
  }

  cancelBonus(element) {
    if(this.disableOnInteraction) return;
    this.openConfirmDialog(cancelConfirmationMsg, {...element, type: 'cancelBonus'}, () => this.confirm);
  }

  activateBonus(element) {
    if(this.disableOnInteraction) return;
    this.openConfirmDialog(activateConfirmationMsg, {...element, type: 'activateBonus'}, () => this.confirm)
  }

  confirm(bonus): void {
    this.disableOnInteraction = true;
    if (bonus.type == 'activateBonus') {
      this.confirmActivation(bonus.id);
    } else {
      this.confirmCancelation(bonus.bonus_ref);
    }
  }

  confirmActivation(bonusId: number) {
    this.bonusQueueService.activateBonus(bonusId).pipe(take(1)).subscribe({
      next: (resp) => {
        this._ngZone.runOutsideAngular(() => {
          this.refreshBonusQueue();
          this.refreshBonusList();
        });
        this.disableOnInteraction = false;
      },
      error: (error) => {
        (error?.error?.error === "Bonus is already applied.")
        ?this.openErrorDialog(errorMessage)
        :this.openErrorDialog(error.message);
        this.disableOnInteraction = false;
      }
    });
  }

  ceilAmount(value: any): number | string {
    const n = Number(value);
    if(n <= 1) return value;
    return isNaN(n) ? 0 : Math.round(n);
  }
}
